import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Download,
  Print,
  Person,
  CalendarToday,
  LocationOn,
  Warning,
  Error as ErrorIcon,
  Info,
  CheckCircle,
  Assignment,
  Security,
  LocalHospital,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface InjuryDetail {
  bodyPart: string;
  injuryType: string;
  severity: 'minor' | 'moderate' | 'severe';
  treatment: string;
  medicalAttention: boolean;
}

interface Witness {
  name: string;
  position: string;
  contactInfo: string;
}

interface CorrectiveAction {
  action: string;
  assignedTo: string;
  dueDate: string;
  status: 'pending' | 'in-progress' | 'completed';
  priority: 'low' | 'medium' | 'high';
}

interface SafetyReportDetail {
  _id: string;
  project: {
    name: string;
    location: string;
  };
  incidentDate: string;
  reportDate: string;
  reportedBy: {
    firstName: string;
    lastName: string;
    position: string;
    avatar?: string;
  };
  incidentType: 'injury' | 'near-miss' | 'property-damage' | 'environmental' | 'security';
  severity: 'low' | 'medium' | 'high' | 'critical';
  location: string;
  description: string;
  injuredPerson?: {
    name: string;
    position: string;
    company: string;
    experience: string;
  };
  injuryDetails?: InjuryDetail;
  witnesses: Witness[];
  immediateActions: string[];
  rootCause: string;
  contributingFactors: string[];
  correctiveActions: CorrectiveAction[];
  preventiveMeasures: string[];
  photos: string[];
  status: 'draft' | 'submitted' | 'under-investigation' | 'closed';
  investigatedBy?: {
    firstName: string;
    lastName: string;
  };
  investigationDate?: string;
  followUpRequired: boolean;
  followUpDate?: string;
  notes: string;
}

const SafetyReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<SafetyReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const isValidObjectId = (val: string) => /^[a-fA-F0-9]{24}$/.test(val);
    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!id || !isValidObjectId(id)) {
          throw new Error('Invalid report ID');
        }
        const response = await api.get(`/safety-reports/${id}`);
        const r = response?.data?.data;
        if (!r) {
          setReport(null);
          return;
        }

        // Map backend schema to UI detail model
        const incidentTypeRaw = r?.incident?.type || r?.reportType || 'incident';
        const incidentType = (
          incidentTypeRaw === 'near_miss' ? 'near-miss' :
          incidentTypeRaw === 'property_damage' ? 'property-damage' :
          incidentTypeRaw === 'environmental' ? 'environmental' :
          incidentTypeRaw === 'injury' ? 'injury' :
          'incident'
        ) as SafetyReportDetail['incidentType'];

        const severityRaw = r?.incident?.severity || 'low';
        const severity = (
          severityRaw === 'minor' ? 'low' :
          severityRaw === 'moderate' ? 'medium' :
          severityRaw === 'major' ? 'high' :
          'critical'
        ) as SafetyReportDetail['severity'];

        const injured = Array.isArray(r?.personsInvolved)
          ? r.personsInvolved.find((p: any) => p?.type === 'injured')
          : null;

        const injuryDetails = injured?.injury
          ? {
              bodyPart: injured?.injury?.bodyPart || '',
              injuryType: injured?.injury?.injuryType || '',
              severity: (injured?.injury?.severity || 'minor') as InjuryDetail['severity'],
              treatment: injured?.injury?.treatmentRequired || '',
              medicalAttention: ['medical_attention','hospitalization','surgery'].includes(injured?.injury?.treatmentRequired || ''),
            }
          : undefined;

        const injuredPerson = injured
          ? {
              name: injured?.externalPerson?.name || '',
              position: injured?.externalPerson?.role || '',
              company: injured?.externalPerson?.company || '',
              experience: '',
            }
          : undefined;

        const witnesses: Witness[] = Array.isArray(r?.witnesses)
          ? r.witnesses.map((w: any) => ({
              name: w?.externalPerson?.name || '',
              position: w?.externalPerson?.company || '',
              contactInfo: w?.externalPerson?.contact?.phone || w?.externalPerson?.contact?.email || '',
            }))
          : [];

        const contributingFactors: string[] = Array.isArray(r?.incident?.contributingFactors)
          ? r.incident.contributingFactors.map((cf: any) => cf?.description || cf?.factor || '').filter(Boolean)
          : [];

        const correctiveActions: CorrectiveAction[] = Array.isArray(r?.correctiveActions)
          ? r.correctiveActions.map((ca: any) => ({
              action: ca?.description || '',
              assignedTo: ca?.assignedTo?.firstName && ca?.assignedTo?.lastName
                ? `${ca.assignedTo.firstName} ${ca.assignedTo.lastName}`
                : '',
              dueDate: ca?.targetDate || '',
              status: (ca?.status || 'pending') as CorrectiveAction['status'],
              priority: (ca?.priority || 'medium') as CorrectiveAction['priority'],
            }))
          : [];

        const statusRaw = r?.status || 'draft';
        const statusMapped = (
          statusRaw === 'under_investigation' ? 'under-investigation' :
          statusRaw === 'closed' ? 'closed' :
          statusRaw === 'submitted' ? 'submitted' :
          'draft'
        ) as SafetyReportDetail['status'];

        const investigatedBy = r?.investigation?.investigator
          ? {
              firstName: r?.investigation?.investigator?.firstName || '',
              lastName: r?.investigation?.investigator?.lastName || '',
            }
          : (r?.investigatedBy ? {
              firstName: r?.investigatedBy?.firstName || '',
              lastName: r?.investigatedBy?.lastName || '',
            } : undefined);

        const detail: SafetyReportDetail = {
          _id: r?._id,
          project: {
            name: r?.project?.name || 'Project',
            location: r?.location?.description || '',
          },
          incidentDate: r?.date || new Date().toISOString(),
          reportDate: r?.createdAt || r?.submittedAt || r?.date || new Date().toISOString(),
          reportedBy: {
            firstName: r?.reportedBy?.firstName || '',
            lastName: r?.reportedBy?.lastName || '',
            position: '',
            avatar: '',
          },
          incidentType,
          severity,
          location: r?.location?.description || '',
          description: r?.incident?.description || r?.notes || '',
          injuredPerson,
          injuryDetails,
          witnesses,
          immediateActions: r?.incident?.immediateActions ? [r.incident.immediateActions] : [],
          rootCause: r?.incident?.rootCause?.analysis || r?.incident?.rootCause?.primary || '',
          contributingFactors,
          correctiveActions,
          preventiveMeasures: [],
          photos: Array.isArray(r?.photos) ? r.photos.map((p: any) => p?.url).filter(Boolean) : [],
          status: statusMapped,
          investigatedBy,
          investigationDate: r?.investigation?.startDate || '',
          followUpRequired: !!r?.followUp?.required,
          followUpDate: r?.followUp?.scheduledDate || '',
          notes: r?.notes || '',
        };

        setReport(detail);
      } catch (err: any) {
        console.error('Failed to load safety report', err);
        setError(err?.message || 'Failed to load safety report');
        setReport(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'closed':
        return 'success';
      case 'under-investigation':
        return 'warning';
      case 'submitted':
        return 'info';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getIncidentTypeIcon = (type: string) => {
    switch (type) {
      case 'injury':
        return <LocalHospital />;
      case 'near-miss':
        return <Warning />;
      case 'property-damage':
        return <ErrorIcon />;
      case 'environmental':
        return <Info />;
      case 'security':
        return <Security />;
      default:
        return <Assignment />;
    }
  };

  const getActionStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'warning';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading safety report...</Typography>
      </Box>
    );
  }

  if (!report) {
    return (
      <Box sx={{ p: 3 }}>
        {error ? (
          <Typography variant="h6" color="error">{error}</Typography>
        ) : (
          <Typography variant="h6" color="error">Safety report not found</Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/safety-reports')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold" sx={{ flex: 1 }}>
          Safety Report Details
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Edit />}>
            Edit
          </Button>
          <Button variant="outlined" startIcon={<Download />}>
            Export
          </Button>
          <Button variant="outlined" startIcon={<Print />}>
            Print
          </Button>
        </Box>
      </Box>

      {/* Alert for high severity incidents */}
      {(report.severity === 'high' || report.severity === 'critical') && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          icon={<Warning />}
        >
          <Typography fontWeight="bold">
            {report.severity === 'critical' ? 'CRITICAL' : 'HIGH SEVERITY'} INCIDENT
          </Typography>
          This incident requires immediate attention and follow-up actions.
        </Alert>
      )}

      {/* Report Info Card */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              src={report.reportedBy.avatar}
              sx={{ width: 60, height: 60, mr: 2 }}
            >
              {report.reportedBy.firstName[0]}{report.reportedBy.lastName[0]}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                {report.project.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reported by: {report.reportedBy.firstName} {report.reportedBy.lastName} ({report.reportedBy.position})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {report.project.location}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={getIncidentTypeIcon(report.incidentType)}
                label={report.incidentType.replace('-', ' ').toUpperCase()}
                color={getSeverityColor(report.severity) as any}
                sx={{ mr: 1 }}
              />
              <Chip
                label={report.status.replace('-', ' ').toUpperCase()}
                color={getStatusColor(report.status) as any}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Incident Date</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatDateTime(report.incidentDate)}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Report Date</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatDateTime(report.reportDate)}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOn fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Location</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {report.location}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Severity</Typography>
                <Typography variant="body2" fontWeight="medium" color={`${getSeverityColor(report.severity)}.main`}>
                  {report.severity.toUpperCase()}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        {/* Left Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Incident Description */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Incident Description
              </Typography>
              <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                {report.description}
              </Typography>
            </CardContent>
          </Card>

          {/* Injured Person Details */}
          {report.injuredPerson && (
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalHospital color="error" />
                  Injured Person
                </Typography>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Name</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {report.injuredPerson.name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Position</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {report.injuredPerson.position}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Company</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {report.injuredPerson.company}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Experience</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {report.injuredPerson.experience}
                    </Typography>
                  </Box>
                </Box>

                {report.injuryDetails && (
                  <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Injury Details
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Body Part:</strong> {report.injuryDetails.bodyPart}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Type:</strong> {report.injuryDetails.injuryType}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Severity:</strong> {report.injuryDetails.severity}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Medical Attention:</strong> {report.injuryDetails.medicalAttention ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      <strong>Treatment:</strong> {report.injuryDetails.treatment}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Root Cause Analysis */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Root Cause Analysis
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Root Cause
                </Typography>
                <Typography variant="body2" sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                  {report.rootCause}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Contributing Factors
                </Typography>
                <List dense>
                  {report.contributingFactors.map((factor, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Warning color="warning" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={factor}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </CardContent>
          </Card>

          {/* Corrective Actions */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Corrective Actions
              </Typography>
              
              {report.correctiveActions.map((action, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      label={action.status.replace('-', ' ').toUpperCase()}
                      color={getActionStatusColor(action.status) as any}
                      size="small"
                    />
                    <Chip
                      label={action.priority.toUpperCase()}
                      color={getSeverityColor(action.priority) as any}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    {action.action}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Assigned To:</strong> {action.assignedTo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Due Date:</strong> {new Date(action.dueDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>

        {/* Right Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Immediate Actions */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Immediate Actions Taken
              </Typography>
              
              <List dense>
                {report.immediateActions.map((action, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={action}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Witnesses */}
          {report.witnesses.length > 0 && (
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  Witnesses
                </Typography>
                
                {report.witnesses.map((witness, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {witness.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {witness.position}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {witness.contactInfo}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Preventive Measures */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Preventive Measures
              </Typography>
              
              <List dense>
                {report.preventiveMeasures.map((measure, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Security color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={measure}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Investigation Details */}
          {report.investigatedBy && (
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  Investigation
                </Typography>
                
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Investigated By</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {report.investigatedBy.firstName} {report.investigatedBy.lastName}
                    </Typography>
                  </Box>
                  
                  {report.investigationDate && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Investigation Date</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatDateTime(report.investigationDate)}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary">Follow-up Required</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {report.followUpRequired ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                  
                  {report.followUpDate && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Follow-up Date</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {new Date(report.followUpDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      {/* Notes */}
      {report.notes && (
        <Card sx={{ mt: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Investigation Notes
            </Typography>
            <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {report.notes}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SafetyReportDetail;